import React from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

const T = {
  teal: '#5EEAD4', tealDark: '#14B8A6',
  lavender: '#C4B5FD', lavenderDark: '#8B5CF6',
  coral: '#FDA4AF', coralDark: '#F43F5E',
  text: '#1E293B', textMuted: '#64748B',
};

const Reveal: React.FC<{ children: React.ReactNode; className?: string; delay?: number }> = ({
  children, className = '', delay = 0,
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const GlassCard: React.FC<{ children: React.ReactNode; className?: string; heavy?: boolean }> = ({
  children, className = '', heavy = false,
}) => (
  <div
    className={`rounded-3xl border shadow-xl ${className}`}
    style={{
      background: heavy ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.7)',
      backdropFilter: heavy ? 'blur(40px) saturate(1.4)' : 'blur(24px) saturate(1.2)',
      WebkitBackdropFilter: heavy ? 'blur(40px) saturate(1.4)' : 'blur(24px) saturate(1.2)',
      borderColor: heavy ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.6)',
      boxShadow: '0 8px 32px rgba(148,163,184,0.12), 0 1px 3px rgba(148,163,184,0.08)',
    }}
  >
    {children}
  </div>
);

const About: React.FC = () => (
  <div className="relative z-10 pt-24">
    {/* Hero */}
    <section className="relative py-28 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <p className="text-sm font-bold tracking-[0.2em] uppercase mb-4" style={{ color: T.teal }}>
            About Karvlo
          </p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-slate-800 leading-[0.92] tracking-tight mb-8">
            We believe riders
            <br />
            <span className="bg-gradient-to-r from-teal-400 via-violet-400 to-rose-400 bg-clip-text text-transparent">
              deserve better.
            </span>
          </h1>
          <p className="text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed">
            Karvlo was born from a simple frustration: why do ride-sharing platforms
            take 30% when technology can do it for 5%? We set out to build the
            fairest ride platform in Hong Kong.
          </p>
        </motion.div>
      </div>
    </section>

    {/* Mission */}
    <section className="relative py-20 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="text-sm font-bold tracking-[0.2em] uppercase mb-4" style={{ color: T.lavender }}>
              Our Mission
            </p>
            <h2 className="text-4xl font-black text-slate-800 leading-tight mb-6">
              Fair rides.
              <br />Fair earnings.
            </h2>
            <p className="text-slate-500 text-lg leading-relaxed mb-6">
              Traditional rideshare platforms take 25–30% of every fare. That's money
              coming out of drivers' pockets. Karvlo flips this: we take only 5%,
              meaning drivers keep 95% of every fare.
            </p>
            <p className="text-slate-500 text-lg leading-relaxed">
              For riders, we eliminate surge pricing entirely and provide complete fare
              transparency before you book. What you see is what you pay — always.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '5%', label: 'Platform Fee', accent: T.teal },
                { value: '95%', label: 'Driver Earnings', accent: T.lavender },
                { value: '0', label: 'Surge Pricing', accent: T.coral },
                { value: '18', label: 'HK Districts', accent: T.tealDark },
              ].map((stat, i) => (
                <GlassCard key={i} className="p-6 text-center">
                  <p className="text-3xl font-black mb-1" style={{ color: stat.accent }}>
                    {stat.value}
                  </p>
                  <p className="text-slate-400 text-sm font-medium">{stat.label}</p>
                </GlassCard>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* Values */}
    <section className="relative py-20 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto">
        <Reveal className="text-center mb-16">
          <p className="text-sm font-bold tracking-[0.2em] uppercase mb-4" style={{ color: T.coral }}>
            Our Values
          </p>
          <h2 className="text-4xl sm:text-5xl font-black text-slate-800 leading-tight">
            What we stand for.
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: '🔍', title: 'Radical Transparency', desc: 'No hidden fees, no surge, no tricks. Every fare breakdown is visible before you tap "Book". Drivers see exactly how much they earn.' },
            { icon: '🤝', title: 'Driver First', desc: "We believe drivers are partners, not contractors to squeeze. 95% take-home rate, weekly payouts, insurance support, and a real voice in platform decisions." },
            { icon: '🏠', title: 'Built for HK', desc: "We're not adapting a global template — Karvlo is built from scratch for Hong Kong's unique streets, districts, and culture. Bilingual, Octopus-ready, Gaode-powered." },
          ].map((value, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <GlassCard className="p-8 h-full">
                <span className="text-4xl mb-4 block">{value.icon}</span>
                <h3 className="text-xl font-bold text-slate-800 mb-3">{value.title}</h3>
                <p className="text-slate-500 leading-relaxed">{value.desc}</p>
              </GlassCard>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* Timeline */}
    <section className="relative py-20 px-4 sm:px-6">
      <div className="max-w-3xl mx-auto">
        <Reveal className="text-center mb-16">
          <h2 className="text-4xl font-black text-slate-800">Our Journey</h2>
        </Reveal>

        <div className="space-y-8">
          {[
            { date: 'Q4 2025', title: 'Idea & Research', desc: 'Studied HK ride-sharing pain points. Interviewed 200+ drivers and riders.' },
            { date: 'Q1 2026', title: 'Platform Built', desc: 'Full-stack development: AI matching, real-time tracking, Stripe payments, bilingual support.' },
            { date: 'Q1 2026', title: 'Open Beta Launch', desc: 'Live in all 18 HK districts. Real drivers, real rides, real impact.' },
            { date: 'Q2 2026', title: 'Public Launch', desc: 'Full launch with Octopus card integration, corporate accounts, and scheduled rides.' },
          ].map((milestone, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-24 text-right">
                  <span className="text-sm font-bold" style={{ color: T.teal }}>{milestone.date}</span>
                </div>
                <div className="w-px bg-slate-200 flex-shrink-0 self-stretch" />
                <div className="flex-1 pb-2">
                  <h3 className="font-bold text-slate-800 mb-1">{milestone.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{milestone.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="relative py-20 px-4 sm:px-6">
      <div className="max-w-3xl mx-auto text-center">
        <Reveal>
          <GlassCard heavy className="p-12">
            <h2 className="text-3xl font-black text-slate-800 mb-4">Join the movement.</h2>
            <p className="text-slate-500 text-lg mb-8">
              Whether you ride or drive, Karvlo is building a fairer future.
            </p>
            <a
              href="/#scene-signup"
              className="inline-flex items-center justify-center px-8 py-4 bg-slate-800 rounded-2xl text-white font-bold text-lg shadow-lg hover:bg-slate-700 transition-all duration-300"
            >
              Join the Beta
            </a>
          </GlassCard>
        </Reveal>
      </div>
    </section>
  </div>
);

export default About;
